import React, { useEffect, useRef } from "react";
import {
  BarChart3,
  Bot,
  MessageCircleMore,
  Send,
  ShieldCheck,
  Users,
  Zap,
  Clock,
  TrendingUp,
  PieChart,
  Eye,
  Target,
  Inbox,
  UserCheck,
  Headphones,
  Lock,
  Fingerprint,
  Shield,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import "./CyberneticBentoGrid.css";

const iconMap = {
  whatsapp: MessageCircleMore,
  broadcast: Send,
  automation: Bot,
  analytics: BarChart3,
  collaboration: Users,
  security: ShieldCheck,
};

const defaultItems = [
  {
    key: "whatsapp",
    title: "WhatsApp Business API",
    description:
      "Official API integration for verified business communications at scale with full Meta compliance.",
    className: "cyber-bento-item--hero",
  },
  {
    key: "broadcast",
    title: "Bulk Broadcasting",
    description:
      "Send personalized messages to thousands instantly with advanced segmentation and scheduling.",
  },
  {
    key: "automation",
    title: "Smart Automation & Chatbots",
    description:
      "Intelligent chatbots and workflow automation to handle routine tasks 24/7 without human intervention.",
  },
  {
    key: "analytics",
    title: "Analytics & Insights",
    description:
      "Comprehensive analytics to track engagement, conversion rates, and ROI in real-time.",
    className: "cyber-bento-item--tall",
  },
  {
    key: "collaboration",
    title: "Team Collaboration",
    description:
      "Unified inbox for your entire team with role-based access and performance tracking.",
    className: "cyber-bento-item--wide",
  },
  {
    key: "security",
    title: "Enterprise Security",
    description:
      "Bank-grade encryption with GDPR compliance and SOC 2 certified infrastructure.",
  },
];

function BentoItem({ className = "", item }) {
  const itemRef = useRef(null);
  const Icon = iconMap[item.key] || ShieldCheck;

  useEffect(() => {
    const element = itemRef.current;
    if (!element) {
      return undefined;
    }

    const handleMouseMove = (event) => {
      const rect = element.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      element.style.setProperty("--mouse-x", `${x}px`);
      element.style.setProperty("--mouse-y", `${y}px`);
    };

    element.addEventListener("mousemove", handleMouseMove);

    return () => {
      element.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <article ref={itemRef} className={`cyber-bento-item ${className}`.trim()}>
      <div className="cyber-bento-item__glow" />
      <div className="cyber-bento-item__content">
        <div className="cyber-bento-item__header">
          <span className="cyber-bento-item__icon" aria-hidden="true">
            <Icon size={22} strokeWidth={2.2} />
          </span>
          <span className="cyber-bento-item__eyebrow">Core capability</span>
        </div>

        <div>
          <h3 className="cyber-bento-item__title">{item.title}</h3>
          <p className="cyber-bento-item__description">{item.description}</p>
        </div>

        {/* Branded visual panels per card */}
        {item.key === "whatsapp" && (
          <div className="cyber-bento-item__visual cyber-bento-item__visual--wa" aria-hidden="true">
            <div className="card-visual__bg" />
            <FaWhatsapp className="wa-visual__logo" />
            <div className="wa-visual__bubble wa-visual__bubble--1">Hi! How can we help?</div>
            <div className="wa-visual__bubble wa-visual__bubble--2">Official Business API</div>
            <div className="card-visual__badge">
              <span className="card-visual__badge-dot card-visual__badge-dot--green" />Meta Verified
            </div>
          </div>
        )}

        {item.key === "broadcast" && (
          <div className="cyber-bento-item__visual cyber-bento-item__visual--broadcast" aria-hidden="true">
            <div className="card-visual__bg" />
            <Send className="card-visual__center-icon" size={56} strokeWidth={1.6} />
            <div className="broadcast-visual__ring broadcast-visual__ring--1" />
            <div className="broadcast-visual__ring broadcast-visual__ring--2" />
            <div className="broadcast-visual__ring broadcast-visual__ring--3" />
            <div className="broadcast-visual__pill broadcast-visual__pill--1"><Zap size={12} /> 10K Sent</div>
            <div className="broadcast-visual__pill broadcast-visual__pill--2"><Clock size={12} /> Scheduled</div>
            <div className="card-visual__badge">
              <span className="card-visual__badge-dot card-visual__badge-dot--orange" />Bulk Ready
            </div>
          </div>
        )}

        {item.key === "automation" && (
          <div className="cyber-bento-item__visual cyber-bento-item__visual--automation" aria-hidden="true">
            <div className="card-visual__bg" />
            <Bot className="card-visual__center-icon" size={56} strokeWidth={1.6} />
            <div className="auto-visual__node auto-visual__node--1">If</div>
            <div className="auto-visual__connector" />
            <div className="auto-visual__node auto-visual__node--2">Then</div>
            <div className="auto-visual__pill auto-visual__pill--1"><Zap size={12} /> 24/7 Active</div>
            <div className="card-visual__badge">
              <span className="card-visual__badge-dot card-visual__badge-dot--purple" />AI Powered
            </div>
          </div>
        )}

        {item.key === "analytics" && (
          <div className="cyber-bento-item__visual cyber-bento-item__visual--analytics" aria-hidden="true">
            <div className="card-visual__bg" />
            <div className="analytics-visual__bars">
              <div className="analytics-visual__bar" style={{ height: '40%' }} />
              <div className="analytics-visual__bar" style={{ height: '65%' }} />
              <div className="analytics-visual__bar" style={{ height: '85%' }} />
              <div className="analytics-visual__bar analytics-visual__bar--accent" style={{ height: '100%' }} />
              <div className="analytics-visual__bar" style={{ height: '72%' }} />
            </div>
            <div className="analytics-visual__pill analytics-visual__pill--1"><TrendingUp size={12} /> +42% ROI</div>
            <div className="analytics-visual__pill analytics-visual__pill--2"><Eye size={12} /> Real-time</div>
            <div className="card-visual__badge">
              <span className="card-visual__badge-dot card-visual__badge-dot--cyan" />Live Dashboard
            </div>
          </div>
        )}

        {item.key === "collaboration" && (
          <div className="cyber-bento-item__visual cyber-bento-item__visual--collab" aria-hidden="true">
            <div className="card-visual__bg" />
            <Inbox className="card-visual__center-icon" size={56} strokeWidth={1.6} />
            <div className="collab-visual__avatar collab-visual__avatar--1"><UserCheck size={16} /></div>
            <div className="collab-visual__avatar collab-visual__avatar--2"><Headphones size={16} /></div>
            <div className="collab-visual__avatar collab-visual__avatar--3"><Users size={16} /></div>
            <div className="collab-visual__pill"><Target size={12} /> Unified Inbox</div>
            <div className="card-visual__badge">
              <span className="card-visual__badge-dot card-visual__badge-dot--blue" />Team Sync
            </div>
          </div>
        )}

        {item.key === "security" && (
          <div className="cyber-bento-item__visual cyber-bento-item__visual--security" aria-hidden="true">
            <div className="card-visual__bg" />
            <Shield className="card-visual__center-icon" size={56} strokeWidth={1.6} />
            <div className="security-visual__lock security-visual__lock--1"><Lock size={14} /></div>
            <div className="security-visual__lock security-visual__lock--2"><Fingerprint size={14} /></div>
            <div className="security-visual__pill security-visual__pill--1">GDPR</div>
            <div className="security-visual__pill security-visual__pill--2">SOC 2</div>
            <div className="card-visual__badge">
              <span className="card-visual__badge-dot card-visual__badge-dot--emerald" />Encrypted
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export function CyberneticBentoGrid({
  title = "Powerful Features for Growing Businesses",
  subtitle = "Everything you need to engage customers and scale your messaging operations",
  items = defaultItems,
}) {
  return (
    <div className="cyber-bento">
      <div className="section-header">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>

      <div className="cyber-bento-grid">
        {items.map((item) => (
          <BentoItem
            key={item.key}
            item={item}
            className={item.className || ""}
          />
        ))}
      </div>
    </div>
  );
}

export default CyberneticBentoGrid;